import React from 'react';
import { motion } from 'framer-motion';
import { Building2, FileText, Mic2, Award, Globe, Users, BookOpen, Presentation } from 'lucide-react';
const organizations = [{
  name: 'Pratt & Whitney',
  logo: 'https://www.google.com/s2/favicons?domain=prattwhitney.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/prattwhitney.com'
}, {
  name: 'FLUOR Constructors',
  logo: 'https://www.google.com/s2/favicons?domain=fluor.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/fluor.com'
}, {
  name: 'Phoenix Constructors JV',
  logo: '',
  initials: 'PC'
}, {
  name: 'Skanska',
  logo: 'https://www.google.com/s2/favicons?domain=skanska.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/skanska.com'
}, {
  name: 'Ford Motor Company',
  logo: 'https://www.google.com/s2/favicons?domain=ford.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/ford.com'
}, {
  name: 'Merrill Lynch',
  logo: 'https://www.google.com/s2/favicons?domain=ml.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/ml.com'
}, {
  name: 'AIG Insurance',
  logo: 'https://www.google.com/s2/favicons?domain=aig.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/aig.com'
}, {
  name: 'Fitch Ratings',
  logo: 'https://www.google.com/s2/favicons?domain=fitchratings.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/fitchratings.com'
}, {
  name: 'POWER Engineers',
  logo: 'https://www.google.com/s2/favicons?domain=powereng.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/powereng.com'
}, {
  name: 'Level 3 Communications',
  logo: 'https://www.google.com/s2/favicons?domain=lumen.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/lumen.com'
}, {
  name: 'Sunoco',
  logo: 'https://www.google.com/s2/favicons?domain=sunoco.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/sunoco.com'
}, {
  name: 'BP',
  logo: 'https://www.google.com/s2/favicons?domain=bp.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/bp.com'
}, {
  name: 'World Trade Center HUB',
  logo: 'https://www.google.com/s2/favicons?domain=wtc.com&sz=128',
  initials: 'WTC'
}, {
  name: 'Eversource',
  logo: 'https://www.google.com/s2/favicons?domain=eversource.com&sz=128',
  fallbackLogo: 'https://logo.clearbit.com/eversource.com'
}];
const publications = ['Developed integrated enterprise scheduling frameworks aligning Primavera + SAP budget control systems', 'Produced executive-level reporting systems improving project visibility and governance', 'Created structured project controls and CPM methodologies adopted across engineering and construction teams', 'Delivered training frameworks and system adoption materials for enterprise PMO environments', 'Built multi-tier financial and forecasting models supporting large-scale operational decision making'];
const speaking = ['Led cross-functional executive reporting and project performance briefings for engineering and finance leadership', 'Delivered training and adoption programs for Primavera enterprise scheduling environments', 'Provided consulting and advisory in project controls, delay claims, and enterprise PMO optimization', 'Mentored project teams in scheduling strategy, resource planning, and performance tracking'];
const visibilityTags = ['Enterprise Project Controls & Scheduling Transformation', 'Infrastructure & Megaproject Delivery Leadership', 'PMO Governance & Performance Optimization', 'Budget Control & Risk-Aligned Project Execution', 'Integrated Scheduling & Critical Path Strategy'];
export function MediaAuthority() {
  return <section id="authority" className="py-20 bg-[#FDFBF7] relative overflow-hidden" data-id="element-277">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-278">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16 text-center" data-id="element-279">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-corporate-teal/10 text-corporate-teal text-xs font-bold uppercase tracking-wider mb-4 border border-corporate-teal/20" data-id="element-280">
            <Award size={14} data-id="element-281" />
            Media / Press / Authority
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-corporate-blue mb-6 leading-tight" data-id="element-282">
            Professional Recognition & Industry Presence
          </h2>
          <p className="text-lg text-[#6B6356] max-w-3xl mx-auto leading-relaxed" data-id="element-283">
            Demonstrated leadership across global engineering, construction,
            finance, and project delivery environments, contributing to
            high-impact programs for Fortune-level organizations, infrastructure
            megaprojects, and enterprise PMO transformations.
          </p>
        </motion.div>

        {/* Organizations Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="mb-20" data-id="element-284">
          <h3 className="text-xl font-bold text-corporate-blue mb-8 text-center flex items-center justify-center gap-2" data-id="element-285">
            <Building2 className="text-corporate-teal" size={20} data-id="element-286" />
            Organizations, Clients & Projects
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-287">
            {organizations.map((org, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-white p-5 rounded-lg border border-[#E8E0D0] shadow-sm flex flex-col items-center justify-center text-center gap-3 hover:shadow-md transition-shadow hover:border-corporate-teal/30 group min-h-[120px]" data-id="element-288">
                {org.logo ? <img src={org.logo} alt={`${org.name} logo`} className="h-12 w-12 object-contain rounded" onError={e => {
              const target = e.target as HTMLImageElement;
              if (org.fallbackLogo && target.src !== org.fallbackLogo) {
                target.src = org.fallbackLogo;
              } else {
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }
            }} data-id="element-289" /> : null}
                {/* Fallback monogram */}
                <div className={`w-12 h-12 rounded-full bg-corporate-teal/10 items-center justify-center flex-shrink-0 ${org.logo ? 'hidden' : 'flex'}`} data-id="element-290">
                  <span className="text-corporate-teal font-bold text-sm" data-id="element-291">
                    {org.initials || org.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <span className="text-xs font-medium text-[#6B6356] group-hover:text-corporate-blue transition-colors leading-tight" data-id="element-292">
                  {org.name}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Two Column Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20" data-id="element-293">
          {/* Publications */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white p-8 rounded-xl border border-[#E8E0D0] shadow-sm relative overflow-hidden" data-id="element-294">
            <div className="absolute top-0 left-0 w-1 h-full bg-corporate-teal" data-id="element-295" />
            <div className="flex items-center gap-3 mb-6" data-id="element-296">
              <div className="w-10 h-10 rounded-lg bg-corporate-teal/10 flex items-center justify-center" data-id="element-297">
                <FileText className="text-corporate-teal" size={20} data-id="element-298" />
              </div>
              <h3 className="text-xl font-bold text-corporate-blue" data-id="element-299">
                Publications & Technical Contributions
              </h3>
            </div>
            <ul className="space-y-4" data-id="element-300">
              {publications.map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#4A4438]" data-id="element-301">
                  <div className="w-1.5 h-1.5 rounded-full bg-corporate-teal mt-2 flex-shrink-0" data-id="element-302" />
                  <span className="leading-relaxed" data-id="element-303">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Speaking */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="bg-white p-8 rounded-xl border border-[#E8E0D0] shadow-sm relative overflow-hidden" data-id="element-304">
            <div className="absolute top-0 left-0 w-1 h-full bg-corporate-blue" data-id="element-305" />
            <div className="flex items-center gap-3 mb-6" data-id="element-306">
              <div className="w-10 h-10 rounded-lg bg-corporate-blue/10 flex items-center justify-center" data-id="element-307">
                <Presentation className="text-corporate-blue" size={20} data-id="element-308" />
              </div>
              <h3 className="text-xl font-bold text-corporate-blue" data-id="element-309">
                Interviews, Speaking & Knowledge Leadership
              </h3>
            </div>
            <ul className="space-y-4" data-id="element-310">
              {speaking.map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#4A4438]" data-id="element-311">
                  <div className="w-1.5 h-1.5 rounded-full bg-corporate-blue mt-2 flex-shrink-0" data-id="element-312" />
                  <span className="leading-relaxed" data-id="element-313">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Visibility Tags */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4
      }} className="text-center" data-id="element-314">
          <h3 className="text-lg font-bold text-corporate-blue mb-6 flex items-center justify-center gap-2" data-id="element-315">
            <Globe className="text-corporate-teal" size={18} data-id="element-316" />
            Media & Industry Visibility
          </h3>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-317">
            {visibilityTags.map((tag, index) => <span key={index} className="px-4 py-2 bg-white border border-[#E8E0D0] rounded-full text-sm text-[#6B6356] font-medium shadow-sm hover:border-corporate-teal/50 hover:text-corporate-blue transition-colors cursor-default" data-id="element-318">
                {tag}
              </span>)}
          </div>
        </motion.div>
      </div>
    </section>;
}